import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Customer } from '@/types';

interface AuthStore {
  customer: Customer | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: () => boolean;
  setAuth: (customer: Customer, token: string, refreshToken?: string | null) => void;
  setCustomer: (customer: Customer) => void;
  setTokens: (token: string, refreshToken: string) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      customer: null,
      token: null,
      refreshToken: null,

      isAuthenticated: () => !!get().token && !!get().customer,

      setAuth: (customer, token, refreshToken) => {
        set({ customer, token, refreshToken: refreshToken ?? null });
      },

      setCustomer: (customer) => {
        set({ customer });
      },

      setTokens: (token, refreshToken) => {
        set({ token, refreshToken });
      },

      logout: () => {
        // Revoke refresh token server-side (fire-and-forget)
        const rt = get().refreshToken;
        if (rt) {
          const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api';
          fetch(`${apiUrl}/auth/revoke`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: rt }),
          }).catch(() => {});
        }

        set({ customer: null, token: null, refreshToken: null });
        // Clear chat session IDs to prevent data leaking to next user
        try {
          const keys = Object.keys(sessionStorage).filter(k => k.startsWith('autofrance-chat-session-'));
          keys.forEach(k => sessionStorage.removeItem(k));
        } catch { /* ignore */ }
        // Clear login count so next user gets fresh greeting
        try { localStorage.removeItem('autofrance-login-count'); } catch { /* ignore */ }
      },
    }),
    {
      name: 'autofrance-auth',
      partialize: (state) => ({ token: state.token, customer: state.customer, refreshToken: state.refreshToken }),
    }
  )
);
